/**
 * Digital Divot downloadable PDF guides catalog.
 * Edit products, pricing, modules, and Stripe env keys here.
 * Add a Master Guide later by appending to GUIDE_PRODUCTS (and updating the bundle if needed).
 */

export const GUIDES_SLUG = 'guides';
export const GUIDES_PATH = `/${GUIDES_SLUG}`;
export const GUIDES_CHECKOUT_PATH = `${GUIDES_PATH}/checkout`;

export const GUIDES_PAGE_TITLE = 'Mobile Golf Simulator Guides';
export const GUIDES_HERO_HEADLINE = 'Start Your Mobile Golf Simulator Business';
export const GUIDES_HERO_SUBHEAD =
  'The exact PDF guides Digital Divot used to build its own mobile golf simulator business, from first idea to a fully equipped setup.';

export const GUIDES_TAGLINE = 'Enjoy Golf Anytime';
export const GUIDES_SUPPORT_EMAIL = 'admin@digitaldivotgolf.com';

/**
 * Optional hosted Payment Links per product id (startup | equipment | bundle).
 * Prefer Checkout Sessions via the API; set these only if you want a direct Payment Link override.
 */
export const GUIDE_STRIPE_CHECKOUT_URLS = {
  startup: process.env.REACT_APP_STRIPE_GUIDE_STARTUP_CHECKOUT_URL || '',
  equipment: process.env.REACT_APP_STRIPE_GUIDE_EQUIPMENT_CHECKOUT_URL || '',
  bundle: process.env.REACT_APP_STRIPE_GUIDE_BUNDLE_CHECKOUT_URL || '',
};

/**
 * Frontend references for Stripe Price IDs (optional).
 * Real Price IDs are resolved server-side from STRIPE_GUIDE_*_PRICE_ID secrets/env.
 */
export const GUIDE_STRIPE_PRICE_IDS = {
  startup: process.env.REACT_APP_STRIPE_GUIDE_STARTUP_PRICE_ID || '',
  equipment: process.env.REACT_APP_STRIPE_GUIDE_EQUIPMENT_PRICE_ID || '',
  bundle: process.env.REACT_APP_STRIPE_GUIDE_BUNDLE_PRICE_ID || '',
};

/** Firebase Storage object paths for PDF delivery (upload these in the Storage console). */
export const GUIDE_STORAGE_PATHS = {
  startup: 'Digital_Divot_Mobile_Golf_Simulator_Startup_Guide_DEEPER_FINAL.pdf',
  equipment: 'Digital_Divot_Equipment_and_Setup_Field_Manual_BALANCED_FINAL.pdf',
};

export const GUIDE_COMING_SOON_NOTE =
  'A Master Guide covering operations, sales, and scaling is coming soon.';

/**
 * Catalog products. Keep ids stable (used by checkout metadata and fulfillment).
 * type: 'guide' = single PDF, 'bundle' = multiple PDFs from includes[].
 */
export const GUIDE_PRODUCTS = [
  {
    id: 'startup',
    type: 'guide',
    title: 'Startup Guide',
    tagline: 'How do I turn this idea into a real business?',
    priceUsd: 150,
    badge: null,
    highlight: false,
    stripePriceEnvKey: 'STRIPE_GUIDE_STARTUP_PRICE_ID',
    storageKey: 'startup',
    includes: ['startup'],
    modules: [
      {
        number: '01',
        title: 'Understand the Business Model',
        description: 'What you are really selling and how clientele shapes the business',
      },
      {
        number: '02',
        title: 'Choose Your Positioning',
        description: 'Decide how you want customers to perceive the company',
      },
      {
        number: '03',
        title: 'Validate Demand Before Overspending',
        description: 'Look for real buying signals before making major investments',
      },
      {
        number: '04',
        title: 'Build a Realistic Startup Budget',
        description: 'The full financial picture and the ~$20K planning benchmark',
      },
      {
        number: '05',
        title: 'Set Up the Business Properly',
        description: 'Business structure, insurance, website, and recordkeeping foundation',
      },
      {
        number: '06',
        title: 'Know Your Basic Operating Requirements',
        description: 'Confirm the concept can realistically fit, travel, operate, and be stored',
      },
      {
        number: '07',
        title: 'Create Your Initial Offer',
        description: 'Turn the concept into a simple customer-facing offer',
      },
      {
        number: '08',
        title: 'Price for Sustainability',
        description: 'Price around cost, customer value, and long-term profitability',
      },
    ],
  },
  {
    id: 'equipment',
    type: 'guide',
    title: 'Equipment & Setup Field Manual',
    tagline: 'What should I actually buy, and how do I put it all together?',
    priceUsd: 150,
    badge: null,
    highlight: false,
    stripePriceEnvKey: 'STRIPE_GUIDE_EQUIPMENT_PRICE_ID',
    storageKey: 'equipment',
    includes: ['equipment'],
    modules: [
      {
        number: '01',
        title: 'Equipment Buying Framework',
        description: 'The 6-point priority system for every purchase decision',
      },
      {
        number: '02',
        title: 'The Four Mission-Critical Components',
        description: 'Launch monitor, enclosure, projector, laptop',
      },
      {
        number: '03',
        title: 'Core Simulator Technology',
        description: 'Launch monitor selection, enclosure design, projector specs, computer specs',
      },
      {
        number: '04',
        title: 'Turf, Screen & Blower',
        description: 'Presentation surfaces, impact screen fit, and inflation systems',
      },
      {
        number: '05',
        title: 'Power, Internet & Lighting',
        description: 'Keeping the system running reliably at every venue',
      },
      {
        number: '06',
        title: 'Anchoring, Weather & Transport',
        description: 'Outdoor operation, mobility, and storage systems',
      },
      {
        number: '07',
        title: 'Clubs, Balls & Software',
        description: 'Player-facing equipment and the software stack that runs events',
      },
      {
        number: '08',
        title: 'Spares, Redundancy & Final Build',
        description: 'What we would build today, plus the complete equipment checklist',
      },
    ],
  },
  {
    id: 'bundle',
    type: 'bundle',
    title: 'Bundle: Both Guides',
    tagline: 'Everything you need to go from idea to fully equipped.',
    priceUsd: 200,
    compareAtUsd: 300,
    savingsUsd: 100,
    badge: 'Best Value',
    highlight: true,
    stripePriceEnvKey: 'STRIPE_GUIDE_BUNDLE_PRICE_ID',
    storageKey: null,
    includes: ['startup', 'equipment'],
    modules: [
      {
        number: '01',
        title: 'Startup Guide (all 8 modules)',
        description: 'Business model, positioning, budget, offer, and pricing',
      },
      {
        number: '02',
        title: 'Equipment & Setup Field Manual (all 8 modules)',
        description: 'Buying framework, mission-critical gear, and full build checklist',
      },
    ],
  },
];

export const getGuideProductById = (id) =>
  GUIDE_PRODUCTS.find((product) => product.id === id) || null;

export const formatGuidePrice = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

/** Inflatable enclosure purchase inquiry (no online payment) */
export const PURCHASE_AGREEMENT_PATH = '/purchase-agreement';

export const INFLATABLE_SECTION = {
  eyebrow: 'Equipment',
  title: 'Purchase Your Enclosure Today!',
  lead:
    'Choose a package, complete the form, and we will follow up to confirm shipping and payment. No charge is processed on this page.',
};

export const INFLATABLE_PACKAGES = [
  {
    id: 'inflatable-only',
    title: 'Inflatable Only',
    priceUsd: 3100,
    tagline: 'The enclosure to get your mobile setup on the road.',
    includes: [
      'Inflatable simulator enclosure',
      'Setup guidance from Digital Divot',
      'Follow-up call to confirm fit for your market',
    ],
    highlight: false,
  },
  {
    id: 'inflatable-guides',
    title: 'Inflatable + Both Guides',
    priceUsd: 3250,
    tagline: 'Enclosure plus the Startup Guide and Equipment Field Manual.',
    includes: [
      'Inflatable simulator enclosure',
      'Startup Guide PDF',
      'Equipment & Setup Field Manual PDF',
      'Priority follow-up to plan your launch',
    ],
    highlight: true,
    badge: 'Most Complete',
  },
];

export const INFLATABLE_PURCHASE_OPTIONS = [
  {
    id: 'card',
    label: 'Credit / Debit Card (+2.9% Processing Fee)',
  },
  {
    id: 'venmo_zelle',
    label: 'Venmo / Zelle (No Processing Fee)',
  },
];

export const INFLATABLE_LOGO_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,image/jpeg,image/png,image/gif';

export const INFLATABLE_LOGO_HINT =
  'PDF, DOC/DOCX, XLS/CSV, JPG/JPEG, PNG, GIF. Company logo required to begin production.';

export const INFLATABLE_EIN_HINT =
  'Required for Air Shipping (8-10 Days). Put "N/A" for shipping by sea (30 days).';

export const INFLATABLE_SMS_TRANSACTIONAL =
  'By checking this box, I consent to receive transactional messages related to my account, orders, or services I have requested. These messages may include appointment reminders, order confirmations, and account notifications among others. Message frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.';

export const INFLATABLE_SMS_MARKETING =
  'By checking this box, I consent to receive marketing and promotional messages, including special offers, discounts, new product updates among others. Message frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.';
